"use client"

import { useEffect, useMemo, useState } from "react"
import { Loader2, MapPin } from "lucide-react"

import { Input } from "@/components/ui/input"
import { useDebounce } from "@/hooks/useDebounce"
import { useGetSearchLocationAutocomplete, useGetSearchLocationResolve } from "openapi/api/endpoints"
import type { GetSearchLocationResolve200Location } from "openapi/api/model"

type AutocompleteSuggestion = {
  placeId: string
  text: string
  primaryText: string
  secondaryText?: string | null
}

export interface ResolvedLocationSelection {
  label: string
  placeId?: string
  formattedAddress?: string | null
  normalizedLabel?: string
  city?: string | null
  region?: string | null
  countryCode?: "US" | "CA" | null
  latitude?: number | null
  longitude?: number | null
  isValidated?: boolean
}

interface LocationAutocompleteInputProps {
  value: string
  onValueChange: (value: string) => void
  onSelectLocation: (location: ResolvedLocationSelection) => void
  countryHint?: "US" | "CA"
  placeholder?: string
}

export function LocationAutocompleteInput({
  value,
  onValueChange,
  onSelectLocation,
  countryHint,
  placeholder = "Start typing a city to see suggestions",
}: LocationAutocompleteInputProps) {
  const [open, setOpen] = useState(false)
  const [pendingSuggestion, setPendingSuggestion] = useState<AutocompleteSuggestion | null>(null)
  const trimmedValue = value.trim()
  const debouncedValue = useDebounce(trimmedValue, 300)

  const { data, isFetching } = useGetSearchLocationAutocomplete(
    {
      input: debouncedValue,
      countryHint,
    },
    {
      query: {
        enabled: open && debouncedValue.length >= 2,
      },
    },
  )

  const suggestions = useMemo(
    () => (data?.suggestions ?? []).filter((suggestion) => suggestion.primaryText),
    [data?.suggestions],
  )

  const { data: resolvedLocationResponse, isFetching: isResolvingLocation } = useGetSearchLocationResolve(
    {
      placeId: pendingSuggestion?.placeId ?? "",
    },
    {
      query: {
        enabled: !!pendingSuggestion?.placeId,
      },
    },
  )

  useEffect(() => {
    if (!pendingSuggestion || isResolvingLocation) return

    const location = resolvedLocationResponse?.location

    onSelectLocation(buildResolvedSelection(pendingSuggestion, location))
    onValueChange("")
    setPendingSuggestion(null)
    setOpen(false)
  }, [isResolvingLocation, onSelectLocation, onValueChange, pendingSuggestion, resolvedLocationResponse?.location])

  const selectSuggestion = (suggestion: AutocompleteSuggestion) => {
    setPendingSuggestion(suggestion)
    setOpen(false)
  }

  const submitCurrentValue = () => {
    if (!trimmedValue) return
    onSelectLocation({
      label: trimmedValue,
      city: trimmedValue,
      normalizedLabel: trimmedValue,
      isValidated: false,
    })
    onValueChange("")
    setOpen(false)
  }

  return (
    <div className="relative">
      <Input
        value={value}
        autoComplete="off"
        placeholder={placeholder}
        onChange={(e) => {
          onValueChange(e.target.value)
          setOpen(true)
        }}
        onFocus={() => {
          if (trimmedValue) {
            setOpen(true)
          }
        }}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 150)
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            if (suggestions.length > 0) {
              selectSuggestion(suggestions[0])
              return
            }
            submitCurrentValue()
          }

          if (e.key === "Escape") {
            setOpen(false)
          }
        }}
      />

      {open && trimmedValue.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-lg">
          {isFetching || isResolvingLocation ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isResolvingLocation ? "Resolving location..." : "Loading location suggestions..."}
            </div>
          ) : suggestions.length > 0 ? (
            <div className="max-h-64 overflow-y-auto py-1">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.placeId}
                  type="button"
                  className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-accent"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{suggestion.primaryText}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {suggestion.secondaryText ?? suggestion.text}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground">No matching locations found.</div>
          )}
        </div>
      )}
    </div>
  )
}

function buildResolvedSelection(
  suggestion: AutocompleteSuggestion,
  resolvedLocation: GetSearchLocationResolve200Location | undefined,
): ResolvedLocationSelection {
  const hasResolvedLocation = resolvedLocation !== undefined && resolvedLocation !== null

  return {
    label: resolvedLocation?.normalizedLabel ?? suggestion.text,
    placeId: resolvedLocation?.placeId ?? suggestion.placeId,
    formattedAddress: resolvedLocation?.formattedAddress ?? suggestion.text,
    normalizedLabel: resolvedLocation?.normalizedLabel ?? suggestion.text,
    city: hasResolvedLocation ? (resolvedLocation?.city ?? null) : suggestion.primaryText,
    region: resolvedLocation?.region,
    countryCode: resolvedLocation?.countryCode ?? null,
    latitude: resolvedLocation?.latitude ?? null,
    longitude: resolvedLocation?.longitude ?? null,
    isValidated: resolvedLocation?.isValidated ?? false,
  }
}
